import { Spinner } from '../../Spinner';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { TASK_DETAILS_PAGE } from '../../../lib/tasks-bi';
import {
  CARD_STATE_KEY,
  dashboardBuildView,
  dashboardPath,
  type DashboardBuildMarker,
} from '../../../lib/dashboard-build';
import { Button } from '../ui';
import styles from './DashboardBuildResultCard.module.css';

// What a dashboard build PRODUCED, in the slot the agent transcript fills on every other task. A build runs
// service-side with no agent conversation, so the transcript panel could only ever show its empty state —
// for every build, finished or not. This reads the row's own marker instead (`display.dashboardBuild`, its
// `result` written by the VMR builder when the run ends): "Building your dashboard…" while it runs, then the
// build's summary, the cards it wrote with their state, and Open dashboard. A FAILED/MOOT row, or an old
// row with no result, leads with its change-log line — a finished build never shows an empty state.
// Mirrored by iOS `DashboardBuildResultSection` and Android `DashboardBuildResultSection`; the reading is
// `lib/dashboard-build.ts`.
export function DashboardBuildResultCard({
  status,
  marker,
  changeLog,
  className,
}: {
  status: string;
  marker?: DashboardBuildMarker;
  changeLog?: readonly { label?: string }[];
  className?: string;
}) {
  const { t } = useTranslation();
  const { logModalActionBi } = useGenericBi('task-details');
  const view = dashboardBuildView(status, marker, changeLog ?? []);
  const dashboard = view.dashboard;

  return (
    <section className={[styles.section, className].filter(Boolean).join(' ')} aria-label={t('tasks.detail.buildResult.title')}>
      <h3 className={styles.title}>{t('tasks.detail.buildResult.title')}</h3>
      {view.kind === 'building' ? (
        <p className={`${styles.line} ${styles.building}`}>
          <Spinner />
          <span>{t('tasks.detail.buildResult.building')}</span>
        </p>
      ) : (
        <>
          {view.messageKey && <p className={styles.line}>{t(view.messageKey)}</p>}
          {/* The build's own words, verbatim — in whatever language the run wrote them. */}
          {view.summary && (
            <p className={styles.summary} dir="auto">
              {view.summary}
            </p>
          )}
          {view.cards.length > 0 && (
            <>
              <h4 className={styles.cardsHeading}>{t('tasks.detail.buildResult.cards')}</h4>
              <ul className={styles.cards}>
                {view.cards.map((card) => (
                  <li key={`${card.dashboard}/${card.slug}`} className={styles.card}>
                    <span className={styles.cardTitle} dir="auto">
                      {card.title}
                    </span>
                    <span className={styles.cardState}>{t(CARD_STATE_KEY[card.state])}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
      {dashboard && (
        <div className={styles.actions}>
          <Button
            variant="outlined"
            size="small"
            href={dashboardPath(dashboard)}
            onClick={() =>
              logModalActionBi({ ...TASK_DETAILS_PAGE, modal_name: 'task_detail', action: 'open_build_dashboard' })
            }
          >
            {t('tasks.detail.buildResult.openDashboard')}
          </Button>
        </div>
      )}
    </section>
  );
}
